document.documentElement.classList.remove("no-js");

const selectors = {
  menuToggle: "[data-menu-toggle]",
  menuClose: "[data-menu-close]",
  productForm: "[data-product-form]",
  cartCount: "[data-cart-count]",
  favourite: "[data-favourite-button]",
  filterToggle: "[data-filter-toggle]",
  filterClose: "[data-filter-close]",
  tabButton: "[data-tab-button]",
  variantSelect: "[data-variant-select]",
  galleryButton: "[data-gallery-button]",
  cartDrawer: "[data-cart-drawer]",
  cartOpen: "[data-cart-open]",
  cartClose: "[data-cart-close]"
};

const shopRoot = () => window.Shopify?.routes?.root || "/";


const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const moneyFi = (cents) => {
  const value = (Number(cents) || 0) / 100;
  return `${value.toFixed(2).replace(".", ",")} €`;
};

const setStatus = (form, message, error = false) => {
  const status = form.querySelector(".form-status");
  if (!status) return;
  status.textContent = message;
  status.dataset.error = error ? "true" : "false";
};

const closePanel = (button, panel) => {
  if (!button || !panel) return;
  panel.hidden = true;
  button.setAttribute("aria-expanded", "false");
  button.focus();
};

const setupMenu = () => {
  const button = document.querySelector(selectors.menuToggle);
  const panel = document.getElementById(button?.getAttribute("aria-controls"));
  const close = panel?.querySelector(selectors.menuClose);
  if (!button || !panel) return;
  button.addEventListener("click", () => {
    panel.hidden = false;
    button.setAttribute("aria-expanded", "true");
    close?.focus();
  });
  close?.addEventListener("click", () => closePanel(button, panel));
  panel.addEventListener("click", (event) => {
    if (event.target === panel) closePanel(button, panel);
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !panel.hidden) closePanel(button, panel);
  });
};

const updateCartCount = (itemCount) => {
  document.querySelectorAll(selectors.cartCount).forEach((count) => {
    count.textContent = String(Number.isFinite(itemCount) ? itemCount : 0);
  });
};

const fetchCart = async () => {
  const response = await fetch(`${shopRoot()}cart.js`, {
    headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new Error("Ostoskoria ei voitu ladata");
  return response.json();
};

const renderCartDrawer = (cart) => {
  const drawer = document.querySelector(selectors.cartDrawer);
  if (!drawer) return;
  const itemsEl = drawer.querySelector("[data-cart-drawer-items]");
  const emptyEl = drawer.querySelector("[data-cart-drawer-empty]");
  const footerEl = drawer.querySelector("[data-cart-drawer-footer]");
  const subtotalEl = drawer.querySelector("[data-cart-subtotal]");
  if (!itemsEl || !emptyEl || !footerEl) return;

  const items = cart.items || [];
  if (!items.length) {
    itemsEl.innerHTML = "";
    emptyEl.hidden = false;
    footerEl.hidden = true;
    updateCartCount(0);
    return;
  }

  emptyEl.hidden = true;
  footerEl.hidden = false;
  if (subtotalEl) subtotalEl.textContent = moneyFi(cart.total_price);
  updateCartCount(cart.item_count);

  itemsEl.innerHTML = items
    .map((item) => {
      const image = item.image
        ? `<img src="${escapeHtml(item.image)}" width="72" height="72" loading="lazy" alt="">`
        : "";
      const variant =
        item.variant_title && item.variant_title !== "Default Title"
          ? `<p class="cart-drawer__variant">${escapeHtml(item.variant_title)}</p>`
          : "";
      return `<article class="cart-drawer__item" data-key="${escapeHtml(item.key)}">
        <a class="cart-drawer__thumb" href="${escapeHtml(item.url)}">${image}</a>
        <div class="cart-drawer__meta">
          <a href="${escapeHtml(item.url)}" class="cart-drawer__title">${escapeHtml(item.product_title)}</a>
          ${variant}
          <div class="cart-drawer__qty" data-cart-qty>
            <button type="button" data-qty-change="-1" aria-label="Vähennä">−</button>
            <span data-qty>${item.quantity}</span>
            <button type="button" data-qty-change="1" aria-label="Lisää">+</button>
          </div>
          <button type="button" class="text-link cart-drawer__remove" data-cart-remove>Poista</button>
        </div>
        <strong class="cart-drawer__line-price" data-line-price>${moneyFi(item.final_line_price)}</strong>
      </article>`;
    })
    .join("");
};

const openCartDrawer = async ({ refresh = true } = {}) => {
  const drawer = document.querySelector(selectors.cartDrawer);
  if (!drawer) return;
  if (refresh) {
    try {
      const cart = await fetchCart();
      renderCartDrawer(cart);
    } catch {
      /* keep SSR markup */
    }
  }
  drawer.hidden = false;
  drawer.querySelector("[data-cart-close]")?.focus();
};

const closeCartDrawer = () => {
  const drawer = document.querySelector(selectors.cartDrawer);
  if (!drawer) return;
  drawer.hidden = true;
};

const changeCartLine = async (key, quantity) => {
  const response = await fetch(`${shopRoot()}cart/change.js`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ id: key, quantity })
  });
  const cart = await response.json();
  if (!response.ok) throw new Error(cart.description || cart.message || "Virhe");
  renderCartDrawer(cart);
  return cart;
};

const setupCartDrawer = () => {
  const drawer = document.querySelector(selectors.cartDrawer);
  if (!drawer) return;

  document.querySelectorAll(selectors.cartOpen).forEach((link) => {
    link.addEventListener("click", (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openCartDrawer({ refresh: true });
    });
  });

  drawer.addEventListener("click", (event) => {
    if (event.target === drawer || event.target.closest(selectors.cartClose)) {
      closeCartDrawer();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !drawer.hidden) closeCartDrawer();
  });

  drawer.addEventListener("click", async (event) => {
    const item = event.target.closest(".cart-drawer__item");
    if (!item) return;
    const key = item.dataset.key;
    if (!key) return;

    const removeBtn = event.target.closest("[data-cart-remove]");
    const qtyBtn = event.target.closest("[data-qty-change]");
    try {
      if (removeBtn) {
        await changeCartLine(key, 0);
        return;
      }
      if (qtyBtn) {
        const delta = Number(qtyBtn.dataset.qtyChange || 0);
        const current = Number(item.querySelector("[data-qty]")?.textContent || 1);
        const next = Math.max(0, current + delta);
        await changeCartLine(key, next);
      }
    } catch (error) {
      console.warn(error);
    }
  });
};

const setupProductForms = () => {
  document.addEventListener("submit", async (event) => {
    const form = event.target.closest(selectors.productForm);
    if (!form || !window.fetch) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const original = button?.textContent;
    if (button) button.disabled = true;
    try {
      const endpoint = `${form.dataset.cartAddUrl}.js`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message);

      let cart;
      try {
        cart = await fetchCart();
      } catch {
        cart = undefined;
      }

      if (cart) {
        renderCartDrawer(cart);
        updateCartCount(cart.item_count);
      } else {
        document.querySelectorAll(selectors.cartCount).forEach((count) => {
          count.textContent = String(
            Number(count.textContent || 0) + Number(result.quantity || 1)
          );
        });
      }

      setStatus(form, form.dataset.successMessage || "Tuote lisättiin ostoskoriin");
      openCartDrawer({ refresh: !cart });
    } catch (error) {
      setStatus(form, error.message || "Tuotetta ei voitu lisätä ostoskoriin", true);
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = original;
      }
    }
  });
};

const favouriteKey = "japaniherkut:favourites";

const readFavourites = () => {
  try {
    const value = JSON.parse(localStorage.getItem(favouriteKey) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const writeFavourites = (values) => {
  try {
    localStorage.setItem(favouriteKey, JSON.stringify(values));
  } catch {
    return;
  }
};

const favouriteState = new Set(readFavourites());

const setupFavourites = () => {
  document.querySelectorAll(selectors.favourite).forEach((button) => {
    if (button.dataset.favouriteBound === "true") return;
    button.dataset.favouriteBound = "true";
    const handle = button.dataset.productHandle;
    button.setAttribute("aria-pressed", favouriteState.has(handle) ? "true" : "false");
    button.addEventListener("click", () => {
      if (favouriteState.has(handle)) favouriteState.delete(handle);
      else favouriteState.add(handle);
      button.setAttribute("aria-pressed", favouriteState.has(handle) ? "true" : "false");
      writeFavourites([...favouriteState]);
    });
  });
};

const setupFilterDrawer = () => {
  const button = document.querySelector(selectors.filterToggle);
  const drawer = document.getElementById(button?.getAttribute("aria-controls"));
  const close = drawer?.querySelector(selectors.filterClose);
  if (!button || !drawer) return;
  button.addEventListener("click", () => {
    drawer.hidden = false;
    button.setAttribute("aria-expanded", "true");
    close?.focus();
  });
  close?.addEventListener("click", () => closePanel(button, drawer));
  drawer.addEventListener("click", (event) => {
    if (event.target === drawer) closePanel(button, drawer);
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !drawer.hidden) closePanel(button, drawer);
  });
};

const setupTabs = () => {
  document.querySelectorAll(selectors.tabButton).forEach((button) => {
    button.addEventListener("click", () => {
      const group = button.closest("[data-tabs]");
      const panel = document.getElementById(button.getAttribute("aria-controls"));
      group?.querySelectorAll(selectors.tabButton).forEach((item) => {
        item.setAttribute("aria-selected", "false");
      });
      group?.querySelectorAll("[data-tab-panel]").forEach((item) => {
        item.hidden = true;
      });
      button.setAttribute("aria-selected", "true");
      if (panel) panel.hidden = false;
    });
  });
};

const setupVariantSelectors = () => {
  document.querySelectorAll("[data-product-root]").forEach((root) => {
    const select = root.querySelector(selectors.variantSelect);
    const form = root.querySelector(selectors.productForm);
    const idInput = form?.querySelector('input[name="id"]');
    const button = form?.querySelector('[type="submit"]');
    const price = root.querySelector("[data-product-price]");
    if (!select || !idInput || !button) return;
    select.addEventListener("change", () => {
      const option = select.selectedOptions[0];
      idInput.value = option.value;
      button.disabled = option.dataset.available !== "true";
      if (price && option.dataset.price) price.textContent = option.dataset.price;
    });
  });
};

const setupGalleries = () => {
  document.querySelectorAll(selectors.galleryButton).forEach((button) => {
    button.addEventListener("click", () => {
      const root = button.closest("[data-gallery]");
      const target = root?.querySelector("[data-gallery-main]");
      const image = button.querySelector("img");
      if (!target || !image) return;
      target.src = button.dataset.fullImage || image.src;
      target.alt = image.alt;
      root.querySelectorAll(selectors.galleryButton).forEach((item) => {
        item.setAttribute("aria-pressed", item === button ? "true" : "false");
      });
    });
  });
};

const setupRecommendations = () => {
  document.querySelectorAll("[data-recommendations]").forEach(async (element) => {
    if (!element.dataset.url || element.innerHTML.trim()) return;
    try {
      const response = await fetch(element.dataset.url);
      if (!response.ok) return;
      const markup = await response.text();
      const documentFragment = new DOMParser().parseFromString(markup, "text/html");
      const replacement = documentFragment.querySelector("[data-recommendations]");
      if (replacement?.innerHTML.trim()) {
        element.innerHTML = replacement.innerHTML;
        setupFavourites();
      }
    } catch {
      return;
    }
  });
};

setupMenu();
setupCartDrawer();
setupProductForms();
setupFavourites();
setupFilterDrawer();
setupTabs();
setupVariantSelectors();
setupGalleries();
setupRecommendations();
